/**
 * 2026-09-28 (D-243) — ops alarm incidents in the admin: Okundu / İncelendi /
 * Ertele.
 *
 * Every alarm the evaluator lists (GET /admin/overview/alarms) is an incident
 * with the operator's marks. An acknowledged (Okundu, or İncelendi which
 * implies it) or currently snoozed (Ertele) incident leaves the global banner
 * and the red bell count and sends no further e-mail; the server is
 * authoritative (POST /admin/overview/incidents/:id/…, provider:inspect,
 * audited). The evaluator merges a mark into the alarms view on its next
 * tick (≤ 60 s); until then the shell overlays the route's answer locally
 * (INCIDENT_PATCH_TTL_MS) so a marked line does not flicker back.
 *
 * Shared by the shell banner, the topbar bell panel and the overview's
 * "Açık olaylar" section.
 */
import { useCallback, useId, useState } from 'react';
import { useApiMutation } from '../api/hooks.js';
import type { ApiClientOptions, ApiResult } from '../api/client.js';
import {
  REVIEW_NOTE_MAX,
  SNOOZE_CHOICES,
  markIncident,
  type IncidentAction,
  type IncidentMarkResult,
  type OverviewAlarm,
  type OverviewAlarmsResponse,
  type OverviewIncident,
} from '../api/overview.js';
import { useCan } from '../session/can.js';
import { ago } from '../i18n/activity-kinds.js';
import { describeProblem, incidentActionErrorText, SEVERITY_LABEL } from '../i18n/overview-problems.js';
import { providerLabel, providerLabels } from '../providers/catalog.js';
import { Badge, Button, Icon, Textarea } from '../ui/index.js';

/** A local mark stays overlaid this long (the evaluator merges it within one 60 s tick). */
export const INCIDENT_PATCH_TTL_MS = 3 * 60_000;

/** The mark fields the overlay may change (from the route's answer). */
export interface IncidentPatch {
  readonly acknowledgedAt?: string | null;
  readonly acknowledgedBy?: string | null;
  readonly reviewedAt?: string | null;
  readonly reviewedBy?: string | null;
  readonly reviewNote?: string | null;
  readonly snoozedUntil?: string | null;
  readonly snoozedBy?: string | null;
}

export interface TimedPatch {
  readonly patch: IncidentPatch;
  /** Client time the patch was recorded (ms). */
  readonly at: number;
}

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Snoozed right now (a past snooze no longer counts). */
export function isSnoozed(alarm: Pick<OverviewAlarm, 'snoozedUntil'>, nowMs: number): boolean {
  const until = ms(alarm.snoozedUntil ?? null);
  return until !== null && until > nowMs;
}

/** Okundu / İncelendi / Ertele: the incident stays listed but out of the banner and silent. */
export function isQuiet(alarm: Pick<OverviewAlarm, 'acknowledgedAt' | 'snoozedUntil'>, nowMs: number): boolean {
  return !!alarm.acknowledgedAt || isSnoozed(alarm, nowMs);
}

/** What the banner and the red bell count show. */
export function isUnacknowledgedCritical(alarm: OverviewAlarm, nowMs: number): boolean {
  return alarm.severity === 'critical' && !isQuiet(alarm, nowMs);
}

export function patchFromIncident(i: OverviewIncident): IncidentPatch {
  return {
    acknowledgedAt: i.acknowledgedAt,
    acknowledgedBy: i.acknowledgedBy,
    reviewedAt: i.reviewedAt,
    reviewedBy: i.reviewedBy,
    reviewNote: i.reviewNote,
    snoozedUntil: i.snoozedUntil,
    snoozedBy: i.snoozedBy,
  };
}

/**
 * Overlays fresh local marks onto the polled view (never mutates it). A patch
 * older than INCIDENT_PATCH_TTL_MS is ignored: by then the server view is
 * authoritative again.
 */
export function applyIncidentPatches(
  data: OverviewAlarmsResponse | null,
  patches: ReadonlyMap<string, TimedPatch>,
  nowMs: number,
): OverviewAlarmsResponse | null {
  if (!data || patches.size === 0) return data;
  let changed = false;
  const alarms = data.alarms.map((a) => {
    const p = a.incidentId ? patches.get(a.incidentId) : undefined;
    if (!p || nowMs - p.at > INCIDENT_PATCH_TTL_MS) return a;
    changed = true;
    return { ...a, ...p.patch };
  });
  return changed ? { ...data, alarms } : data;
}

function clockTr(iso: string | null | undefined): string {
  const t = ms(iso);
  if (t === null) return '—';
  return new Date(t).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

/** One Turkish status line: "Okundu · izzet · 5 dk önce", "Ertelendi · 14:30'a kadar", … */
export function incidentStateText(alarm: OverviewAlarm, nowMs: number): string | null {
  const parts: string[] = [];
  if (alarm.reviewedAt) {
    parts.push(`İncelendi${alarm.reviewedBy ? ` · ${alarm.reviewedBy}` : ''} · ${ago(alarm.reviewedAt)}`);
  } else if (alarm.acknowledgedAt) {
    parts.push(`Okundu${alarm.acknowledgedBy ? ` · ${alarm.acknowledgedBy}` : ''} · ${ago(alarm.acknowledgedAt)}`);
  }
  if (isSnoozed(alarm, nowMs)) {
    parts.push(`Ertelendi · ${clockTr(alarm.snoozedUntil)} kadar${alarm.snoozedBy ? ` (${alarm.snoozedBy})` : ''}`);
  }
  if (alarm.emailed) parts.push('e-posta gönderildi');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Newest-first priority: unacknowledged critical → other unacknowledged → quiet ones. */
export function sortIncidents(alarms: readonly OverviewAlarm[], nowMs: number): OverviewAlarm[] {
  const rank = (a: OverviewAlarm) => (isQuiet(a, nowMs) ? 2 : a.severity === 'critical' ? 0 : 1);
  return alarms
    .map((a, i) => ({ a, i }))
    .sort((x, y) => rank(x.a) - rank(y.a) || x.i - y.i)
    .map((x) => x.a);
}

type MarkInput = {
  readonly incidentId: string;
  readonly action: IncidentAction;
  readonly note?: string | null;
  readonly minutes?: number;
};

const runMark = (o: ApiClientOptions, i: MarkInput): Promise<ApiResult<IncidentMarkResult>> =>
  markIncident(o, i.incidentId, i.action, {
    ...(i.note !== undefined ? { note: i.note } : {}),
    ...(i.minutes !== undefined ? { minutes: i.minutes } : {}),
  });

type Panel = 'none' | 'review' | 'snooze';

export interface IncidentActionsProps {
  readonly alarm: OverviewAlarm;
  /** Called with the server's answer (the shell overlays it). */
  readonly onMarked?: (incidentId: string, patch: IncidentPatch) => void;
  /** Only the Okundu button (the banner). */
  readonly acknowledgeOnly?: boolean;
  readonly testIdPrefix?: string;
}

/**
 * Okundu / İncelendi / Ertele for one incident. Rendered only for admins with
 * provider:inspect and when the incident has a stored row (the table may be
 * missing before migration 0082 — the caller explains that once).
 */
export function IncidentActions({
  alarm,
  onMarked,
  acknowledgeOnly = false,
  testIdPrefix = 'incident',
}: IncidentActionsProps): JSX.Element | null {
  const canInspect = useCan('provider:inspect');
  const { run } = useApiMutation<MarkInput, IncidentMarkResult>(runMark);
  const [busy, setBusy] = useState<IncidentAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>('none');
  const [note, setNote] = useState(alarm.reviewNote ?? '');
  const noteId = useId();
  const incidentId = alarm.incidentId ?? null;

  const submit = useCallback(
    async (action: IncidentAction, extra: { note?: string | null; minutes?: number } = {}) => {
      if (!incidentId) return;
      setBusy(action);
      setError(null);
      const res = await run({ incidentId, action, ...extra });
      setBusy(null);
      if (res.kind === 'ok') {
        setPanel('none');
        onMarked?.(incidentId, patchFromIncident(res.data.incident));
        return;
      }
      setError(
        incidentActionErrorText(
          res.kind === 'error' ? res.reason : res.kind === 'auth_required' ? 'auth_required' : 'unknown',
        ),
      );
    },
    [incidentId, run, onMarked],
  );

  if (!canInspect || !incidentId) return null;
  const acknowledged = !!alarm.acknowledgedAt;
  const id = `${testIdPrefix}-${incidentId}`;
  const trimmed = note.trim();

  return (
    <div className="inc-actions" data-testid={`${id}-actions`}>
      <div className="inc-actions-row">
        {!acknowledged ? (
          <Button
            size="sm"
            variant={acknowledgeOnly ? 'secondary' : 'primary'}
            icon={<Icon.Check size={13} />}
            loading={busy === 'acknowledge'}
            disabled={busy !== null}
            onClick={() => void submit('acknowledge')}
            data-testid={`${id}-ack`}
            title="Olayı okundu olarak işaretle: üst uyarıdan kalkar, bu olay için başka e-posta gelmez"
          >
            Okundu
          </Button>
        ) : null}
        {!acknowledgeOnly ? (
          <>
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon.Pencil size={13} />}
              disabled={busy !== null}
              aria-expanded={panel === 'review'}
              onClick={() => {
                setError(null);
                setPanel(panel === 'review' ? 'none' : 'review');
              }}
              data-testid={`${id}-review`}
            >
              İncelendi
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<Icon.Clock size={13} />}
              disabled={busy !== null}
              aria-expanded={panel === 'snooze'}
              onClick={() => {
                setError(null);
                setPanel(panel === 'snooze' ? 'none' : 'snooze');
              }}
              data-testid={`${id}-snooze`}
            >
              Ertele
            </Button>
          </>
        ) : null}
      </div>
      {panel === 'review' ? (
        <div className="inc-panel" data-testid={`${id}-review-panel`}>
          <label className="inc-panel-label" htmlFor={noteId}>
            İnceleme notu (isteğe bağlı)
          </label>
          <Textarea
            id={noteId}
            rows={2}
            value={note}
            maxLength={REVIEW_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ne bulundu, ne yapıldı?"
            data-testid={`${id}-review-note`}
          />
          <div className="inc-panel-foot">
            <span className="ov-muted ov-xs">
              {trimmed.length}/{REVIEW_NOTE_MAX} · incelendi işareti okundu da sayılır
            </span>
            <span className="inc-panel-buttons">
              <Button size="sm" variant="ghost" onClick={() => setPanel('none')} disabled={busy !== null}>
                Vazgeç
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={busy === 'review'}
                disabled={busy !== null}
                onClick={() => void submit('review', { note: trimmed.length > 0 ? trimmed : null })}
                data-testid={`${id}-review-save`}
              >
                Kaydet
              </Button>
            </span>
          </div>
        </div>
      ) : null}
      {panel === 'snooze' ? (
        <div className="inc-panel inc-panel--snooze" role="group" aria-label="Erteleme süresi" data-testid={`${id}-snooze-panel`}>
          <span className="ov-muted ov-xs">Bu süre boyunca üst uyarıda görünmez ve e-posta gelmez:</span>
          <span className="inc-panel-buttons">
            {SNOOZE_CHOICES.map((c) => (
              <Button
                key={c.minutes}
                size="sm"
                variant="secondary"
                loading={busy === 'snooze'}
                disabled={busy !== null}
                onClick={() => void submit('snooze', { minutes: c.minutes })}
                data-testid={`${id}-snooze-${c.minutes}`}
              >
                {c.label}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setPanel('none')} disabled={busy !== null}>
              Vazgeç
            </Button>
          </span>
        </div>
      ) : null}
      {error ? (
        <div className="inc-error" role="alert" data-testid={`${id}-error`}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

export interface IncidentRowProps {
  readonly alarm: OverviewAlarm;
  readonly nowMs: number;
  readonly onMarked?: (incidentId: string, patch: IncidentPatch) => void;
  /** Hide the description (the bell panel). */
  readonly compact?: boolean;
  readonly testIdPrefix: string;
}

/** One incident: severity, Turkish title, providers, age, state line, the three actions. */
export function IncidentRow({ alarm, nowMs, onMarked, compact = false, testIdPrefix }: IncidentRowProps): JSX.Element {
  const single = alarm.providerIds.length === 1 ? (alarm.providerIds[0] ?? null) : null;
  const text = describeProblem(alarm.code, alarm.params, single);
  const titled = single !== null && text.title.startsWith(`${providerLabel(single)}:`);
  const quiet = isQuiet(alarm, nowMs);
  const state = incidentStateText(alarm, nowMs);
  const tone = alarm.severity === 'critical' ? (quiet ? 'neutral' : 'danger') : 'warn';
  return (
    <li
      className={`inc-row inc-sev-${alarm.severity}${quiet ? ' inc-row--quiet' : ''}`}
      data-testid={`${testIdPrefix}-row-${alarm.id}`}
      data-quiet={quiet ? 'true' : undefined}
    >
      <div className="inc-row-title">
        <Badge tone={tone}>{SEVERITY_LABEL[alarm.severity]}</Badge>
        {alarm.providerIds.length > 0 && !titled ? <Badge tone="neutral">{providerLabels(alarm.providerIds)}</Badge> : null}
        <span className="inc-row-text">{text.title}</span>
        {alarm.since ? (
          <span className="inc-row-since">
            <Icon.Clock size={11} />
            {ago(alarm.since)} başladı
          </span>
        ) : null}
      </div>
      {!compact ? <div className="inc-row-desc">{text.description}</div> : null}
      {state ? (
        <div className="inc-row-state" data-testid={`${testIdPrefix}-state-${alarm.id}`}>
          <Icon.Info size={12} /> {state}
          {alarm.reviewNote ? <span className="inc-row-note"> — “{alarm.reviewNote}”</span> : null}
        </div>
      ) : null}
      <IncidentActions alarm={alarm} onMarked={onMarked} testIdPrefix={testIdPrefix} />
    </li>
  );
}
